import { useRequest } from 'ahooks';
import type { ILegend } from 'bizcharts/lib/components/Legend';
import type { IChartProps } from 'bizcharts/lib/interface';
import classNames from 'classnames';
import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Chart from '@/business-components/chart';
import ChartHeader from '@/business-components/chart-header';
import LockedCard, { LockCardState } from '@/business-components/LockedCard';
import LineAndBarMixedChart from '@/components/charts/LineAndBarMixedChart';
// import LineChart from '@/components/charts/line';
import TitleTag from '@/components/titletag';
import TooltipBase from '@/components/tooltip';
import { RootState } from '@/models';
import { ConBrandWebController } from '@/services/swagger/ConBrandWebController';
import 'rc-tabs/assets/index.css';
import styles from './index.module.scss';

const controller = new ConBrandWebController();
const scale: IChartProps['scale'] = {
  countTimeStr: {
    type: 'timeCat',
    alias: '月份',
    formatter(value) {
      const v = dayjs(value).format('M');
      return `${v}月`;
    },
  },
  value: {
    type: 'linear',
    // min: 0,
    // max: 100,
    tickCount: 2,
    nice: true,
    alias: '近一年非大促期销售额占比',
    formatter(value) {
      return `${value}%`;
    },
  },
};
const legendProps: ILegend = {
  padding: [10, 0, 8, 4],
  // marker: {
  //   symbol: (x, y) => {
  //     const r = 2;
  //     return [
  //       ['M', x - 4 * r, y],
  //       ['L', x + 4 * r, y],
  //       ['M', x - r, y],
  //       ['A', r, r, 0, 0, 0, x + r, y],
  //       ['A', r, r, 0, 0, 0, x - r, y],
  //     ];
  //   },
  //   // style(style) {
  //   //   const { stroke } = style;
  //   //   return { ...style, fill: stroke };
  //   // },
  // },
  itemName: {
    // formatter() {
    //   return '综合指数';
    // },
    style: {
      fill: '#999',
    },
  },
};
export type OperateProps = {
  /** 品牌id */
  conBrandId?: string;
  /** 品牌解锁状态 */
  brandLockState?: string;
  /** 品牌解锁状态改变回调 */
  onLockedCardStateChange?: (state: string) => void;
};

const Operate: React.FC<OperateProps> = ({ conBrandId, brandLockState = 'not_member', onLockedCardStateChange }) => {
  const { token } = useSelector((state: RootState) => state.user);
  /** 经营能力 */
  const { data: operation } = useRequest(
    async () => {
      const res = await controller.conBrandWebControllerV1BusinessCapacityUsingGet1({
        conBrandId,
      });
      const nonBigPromotionList = res?.data?.data?.nonBigPromotionList
        ?.map(item => {
          return {
            ...item,
            dataTypeName: '近一年非大促期销售额占比',
          };
        })
        ?.sort((a, b) => a.countTimeMonth - b.countTimeMonth);
      return { ...res.data.data, nonBigPromotionList };
    },
    {
      refreshDeps: [brandLockState],
    }
  );
  const revenueAmountData = useMemo(() => operation?.revenueAmount?.toFixed(2), [operation?.revenueAmount]);
  const onStateChange = useCallback(
    (state: string) => {
      onLockedCardStateChange?.(state);
    },
    [onLockedCardStateChange]
  );

  // console.log('operation?.nonBigPromotionList', operation?.nonBigPromotionList);
  return (
    !!token && (
      <>
        <ChartHeader
          title={
            <h1 className={styles.moduleTitle}>
              经营能力
              <TooltipBase
                title={
                  <>
                    <p>由经营规模、经营增长能力等维度搭建的数学模型计算得出。</p>
                    <p>数据来源：任拓情报通</p>
                  </>
                }
                placement="right"
              />
            </h1>
          }
          titleFontSize={18}
          className={styles.header}
        />
        {brandLockState !== 'unlocked' ? (
          <LockedCard
            conBrandId={conBrandId}
            backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FhEMiGCPIKEzA1tk6g0Mk7-Pngw8.png"
            explanation="此处为品牌经营规模数据以及非大促销售额占比等经营能力数据，若想查看该信息，请先开通中国新消费品牌数据库会员"
            state={brandLockState as LockCardState}
            className={styles.operateAbilityLockedCard}
            explanationClassName={styles.operateAbilityExplanation}
            onStateChange={onStateChange}
          />
        ) : (
          <div className={classNames('flex-row-nowrap', 'flex-between-start', styles.organize, styles.gapStyle)}>
            <Chart
              title={
                <h2 className={styles.chartTitle}>
                  经营规模
                  <TooltipBase
                    title={
                      <>
                        <p>当月线上营收指数由品牌天猫渠道的预估销售额数据脱敏计算得出。</p>
                        <p>数据来源：任拓情报通</p>
                      </>
                    }
                    placement="right"
                  />
                </h2>
              }
              titleFontSize={16}
              dateSubTitle={false}
              panelClassName={styles.leftContent}
              className={styles.title}
            >
              <div className={styles.card}>
                <TitleTag style={{ marginBottom: '10px' }}>当月线上营收指数</TitleTag>
                <div className={styles.num}>
                  {!operation?.revenueAmount ? (
                    <span className={styles.emptyStateText}>暂无数据</span>
                  ) : (
                    <>
                      <span>{revenueAmountData}</span>
                      {/*<div className={styles.unit}>{`${revenueAmountData.unit}元`}</div>*/}
                    </>
                  )}
                </div>
              </div>
              <div className={styles.card} style={{ marginTop: '14px' }}>
                <TitleTag style={{ marginBottom: '10px' }}>当月线上营收环比增速</TitleTag>
                <div className={styles.num}>
                  {!operation?.revenueRate ? (
                    <span className={styles.emptyStateText}>暂无数据</span>
                  ) : (
                    <span>{operation?.revenueRate}%</span>
                  )}
                </div>
              </div>
            </Chart>
            <Chart
              title={
                <h2 className={styles.chartTitle}>
                  非大促期销售额占比
                  <TooltipBase
                    title={
                      <>
                        <p>
                          品牌某月近1年（不含6月、11月）总销售额占比，反映该品牌旗下产品除618和双11大促外的平日销售情况。
                        </p>
                        <p>数据来源：任拓情报通</p>
                      </>
                    }
                    placement="right"
                  />
                </h2>
              }
              titleFontSize={16}
              dateSubTitle={false}
              panelClassName={styles.rightContent}
            >
              <LineAndBarMixedChart
                height={204}
                // lineChartData={operation?.nonBigPromotionList}
                barChartData={operation?.nonBigPromotionList}
                xAxisName="countTimeStr"
                // yAxisNameOfLineChart="value"
                yAxisNameOfBarChart="value"
                scale={scale}
                interverProps={{ size: 80, color: ['dataTypeName', '#f75c2f'] }}
                lineProps={{ color: ['dataTypeName', '#f75c2f'] }}
                pointProps={{ size: 3, color: ['dataTypeName', '#f75c2f'] }}
                legendProps={legendProps}
              />

              {/* <LineChart
                data={operation?.nonBigPromotionList || []}
                xField="countTimeStr"
                yField="value"
                lField="#f75c2f"
                yLabel={'%'}
                tooltipName="销售额占比"
                height={154}
              /> */}
            </Chart>
          </div>
        )}
      </>
    )
  );
};

export default memo(Operate);
